import React from 'react'
import { NextImage } from '@/components/NextImage'
import { TeamCrestLink } from '@/components/TeamCrestLink'
import { useImage } from '@/hooks/images/useImage'
import { useTranslation } from '@/localization/useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBolt, faLock, faTrophy } from '@fortawesome/free-solid-svg-icons'
import { useEventDate } from '@/hooks/utils/useEventDate'
import { useTournaments } from '@/hooks/tournaments/useTournaments'
import { getTournamentName } from '@/utils/events/getTournamentName'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface EventCardProps {
  item: any
  handleCardClick: (item: any) => void
  isPlaceholder?: boolean
  isLocked?: boolean
}

export const EventCard = (props: EventCardProps) => {
  const {
    item,
    handleCardClick,
    isPlaceholder,
    isLocked = false
  } = props
  const { t } = useTranslation()
  const { data: tournaments, isPlaceholderData } = useTournaments()
  // The catalog only resolves string-id refs, and is skipped while it's still
  // placeholder data: its fake ids never match.
  const tournamentName = getTournamentName(item, isPlaceholderData ? [] : tournaments)
  const homeTeam = item?.homeTeam
  const awayTeam = item?.awayTeam
  // Only a rail scoped to one club (the team page) sets `favoriteTeamId`; there
  // the other club's crest leads to its own page. Every other rail keeps the
  // whole card as a single target.
  const favoriteTeamId = isPlaceholder ? undefined : item?.favoriteTeamId
  const linkedHomeTeam = favoriteTeamId && homeTeam?._id !== favoriteTeamId ? homeTeam : undefined
  const linkedAwayTeam = favoriteTeamId && awayTeam?._id !== favoriteTeamId ? awayTeam : undefined
  const { url: homeTeamFlag } = useImage({
    id: homeTeam?.flag || '',
    cut: 'square',
    scale: '25',
  })

  const { url: awayTeamFlag } = useImage({
    id: awayTeam?.flag,
    cut: 'square',
    scale: '25',
  })

  const { date, isLive, isToday } = useEventDate({
    event: item,
    format: 'date-time'
  })

  if (!item) return null

  return (
    <div
      className={classNames({
        [classes.card]: true,
        [classes['card--placeholder']]: isPlaceholder
      })}
      onClick={() => handleCardClick(item)}
    >
      <div className={classes.header}>
        <div className={classNames({
          [classes.date]: true,
          [classes.date__today]: isToday,
          [classes.date__live]: isLive,
        })}>
          {date}
        </div>
        {tournamentName && (
          <div className={classes.tournament}>
            <FontAwesomeIcon icon={faTrophy} size={'xs'} />
            <span className={classes.tournament__name}>
              {t(tournamentName)}
            </span>
          </div>
        )}
      </div>
      <div className={classes.body}>
        <div className={classNames({
          [classes.date]: true,
          [classes.date__today]: isToday,
          [classes.date__live]: isLive,
        })}>
          {isLocked ?
            <FontAwesomeIcon icon={faLock} /> :
            isLive &&
            <FontAwesomeIcon icon={faBolt} />
          }
          <span>
            {date}
          </span>
        </div>
        <div className={classNames({
          [classes.teams]: true,
          [classes['teams--placeholder']]: isPlaceholder
        })}>
          <div className={classes['teams__home--logo']}>
            <TeamCrestLink team={linkedHomeTeam}>
              <NextImage
                src={homeTeamFlag}
                alt={homeTeam?.name || 'Home Team'}
                width={56}
                height={56}
              />
            </TeamCrestLink>
          </div>
          <div className={classes['teams__away--logo']}>
            <TeamCrestLink team={linkedAwayTeam}>
              <NextImage
                src={awayTeamFlag}
                alt={awayTeam?.name || 'Away Team'}
                width={56}
                height={56}
              />
            </TeamCrestLink>
          </div>
          <div className={classes['teams__home--name']}>
            {homeTeam?.shortName}
          </div>
          <div className={classes['teams__away--name']}>
            {awayTeam?.shortName}
          </div>
        </div>
      </div>
      {tournamentName && (
        <div className={classes.footer}>
          <div className={classes.tournament}>
            <div className={classes.tournament__name}>
              {t(tournamentName)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}